import { mvc, dia } from "@joint/core";
import { getConfig } from "../../../../config";
import { MessageEventBus } from "../../../event-bus";
import { AvoidService } from "./avoid-service";
import RappidContext from "../../rappid/rappid-context";
import { NamedRouter, Transition } from "../../mvc/transition";

const { layoutEnabled } = getConfig();

export class RouterController {
  /** is there at least one link that wants avoid routing */
  isDynamicAvoidRoutingActive = false;

  /** did we encounter an (likely) unrecoverable error in the avoid lib  */
  isAvoidLibEmergency = false;

  routerService: AvoidService;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  graphListener?: mvc.Listener<any>;
  context: RappidContext;

  /** Moving nodes manually triggers re-routing. This may be slow.
   * In this case, we debounce the routing.  */
  isDebounceMode = false;

  // timers to debounce onCellChanged per cell
  pendingChangeTimers: Map<string, number> = new Map();
  // debounce delay in ms (tweak as needed)
  readonly CHANGE_DEBOUNCE_MS = 50;

  constructor(context: RappidContext) {
    this.context = context;
    this.routerService = new AvoidService(context);
    this.addGraphListeners();
  }

  addGraphListeners(): void {
    this.removeGraphListeners();
    if (!layoutEnabled) {
      // honor the feature toggle
      return;
    }
    if (this.isAvoidLibEmergency) {
      return;
    }
    const withErrorHandling = <T extends unknown[]>(handler: (...args: T) => void) => {
      return (...args: T) => {
        try {
          handler(...args);
        } catch (error) {
          if (this.isDynamicAvoidRoutingActive) {
            MessageEventBus.emit("error", {
              text: "Error in dynamic link routing, switching it off.",
              id: "router-controller.listener",
            });
          }
          // eslint-disable-next-line no-console
          console.error(error);
          this.isAvoidLibEmergency = true;
        }
      };
    };
    const listener = new mvc.Listener();
    listener.listenTo(this.context.graph, {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      remove: withErrorHandling((cell: dia.Cell) => this.onCellRemoved(cell)),
      add: withErrorHandling((cell: dia.Cell) => this.onCellAdded(cell)),
      change: withErrorHandling((cell: dia.Cell, opt: any) => this.onCellChanged(cell, opt)),
      reset: withErrorHandling((graph: dia.Graph, opt: any) => this.onGraphReset(graph, opt)),
      /* eslint-enable */
    });
    this.graphListener = listener;
  }

  removeGraphListeners(): void {
    this.graphListener?.stopListening();
    delete this.graphListener;
  }

  /** Complete synch of JointJS-Graph and LibAvoid Graph + route all links */
  routeAll(): void {
    this.routerService.routeAll(true);
  }

  setRouterForAllLinks(router: NamedRouter) {
    const graph = this.context.graph;
    try {
      this.removeGraphListeners();
      this.context.commandManager.initBatchCommand();
      graph?.getLinks().forEach((link) => {
        (link as Transition).setRouter(router);
      });
      this.handleRouterOrchestrationChange();
      this.context.commandManager.storeBatchCommand();
    } catch (error) {
      MessageEventBus.emit("error", {
        text: "Failed to set router for all links.",
        id: "router-controller.setRouterForAllLinks",
      });
      this.context.commandManager.storeBatchCommand();
      this.context.commandManager.undo();
    } finally {
      this.addGraphListeners();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onGraphReset(graph: dia.Graph, { previousModels }: { previousModels: any }): void {
    const isFirstReset = !previousModels || previousModels.length === 0;
    if (!isFirstReset) {
      if (this.routerService.graph !== graph) {
        this.routerService.destroy();
        this.routerService = new AvoidService(this.context);
      }
      this.isDynamicAvoidRoutingActive = false;
    }
    this.handleRouterOrchestrationChange();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCellChanged(cell: dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions>, opt: any): void {
    if (opt.avoidRouter) return;
    if (cell.isLink && "router" in cell.changed) {
      this.handleRouterOrchestrationChange();
      if (this.isDynamicAvoidRoutingActive && !cell.get("router")) {
        // it may be the case that the link was routed by libavoid before (and thus is cached)
        // in order to enforce a re-routing, we need to remove route from cache
        this.routerService.cachedRoutes.delete(cell.id);
      }
    }
    if (this.isDynamicAvoidRoutingActive) {
      const isElementMove = cell.isElement && ("position" in (cell.changed || {}) || "size" in (cell.changed || {}));
      if (isElementMove && this.isDebounceMode) {
        // cancel previous timer (if any)
        const existing = this.pendingChangeTimers.get(cell.id as string);
        if (existing) {
          clearTimeout(existing);
        }
        const timer = window.setTimeout(() => {
          this.pendingChangeTimers.delete(cell.id as string);
          this.routerService.onCellChanged(cell, opt);
        }, this.CHANGE_DEBOUNCE_MS);
        this.pendingChangeTimers.set(cell.id as string, timer);
      } else {
        this.routerService.onCellChanged(cell, opt);
      }
    }
  }

  onCellAdded(cell: dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions>): void {
    if (cell.isLink && cell.get("router")) {
      this.handleRouterOrchestrationChange();
    }
    if (this.isDynamicAvoidRoutingActive) {
      this.routerService.onCellAdded(cell);
    }
  }

  onCellRemoved(cell: dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions>): void {
    if (cell.isLink) {
      this.handleRouterOrchestrationChange();
    }
    if (this.isDynamicAvoidRoutingActive) {
      this.routerService.onCellRemoved(cell);
    }
  }

  private handleRouterOrchestrationChange() {
    // Note: Here's potential for perftuning:
    // Instead of counting the links w/o router each time,
    // we could just count on init and the adjust the number on graph changes
    // BUT: I wanted to add not too much state to this controller.
    const links = this.context.graph.getLinks();
    const noOfLinksToBeRoutedByAvoid = links.filter((link) => !link.get("router")).length;
    if (noOfLinksToBeRoutedByAvoid === 0) {
      this.isDynamicAvoidRoutingActive = false;
    } else {
      if (!this.isDynamicAvoidRoutingActive) {
        this.isDynamicAvoidRoutingActive = true;
        this.routerService.routeAll(false);
      }
      if (links.length > 50) {
        // debouncing is set based on no of all links (not only on no of links routed by libavoid)
        // reason: the actual bottelneck in is libavoid, not in taking over the routes.
        // actually, it is in avoidRouter.processTransaction();
        this.isDebounceMode = true;
      }
    }
  }
}
